import { Context, PoserMode } from "../context";
import { CameraMode, CameraLandmark } from "../camera/controller";
import { PoserApp } from "../poser";
import { WebGLRenderer } from "three";
import { PoserClock } from "../util/clock";

export type PublicCameraOption = "action" | "batter" | "pitcher" | "free";
export interface PublicCameraConfig {
  type: PublicCameraOption;
  key: string;
  title: string;
}

export const CAMERA_CONFIGS: PublicCameraConfig[] = [
  {
    type: "action",
    title: "FieldVision Cam",
    key: "fieldvision_camera_fieldvision",
  },
  {
    type: "pitcher",
    title: "Pitcher Cam",
    key: "fieldvision_camera_pitcher",
  },
  {
    type: "batter",
    title: "Batter Cam",
    key: "fieldvision_camera_batter",
  },
  {
    type: "free",
    title: "Fan Control",
    key: "fieldvision_camera_fan",
  },
];

export class FieldVisionController {
  #app: PoserApp;
  #context: Context;

  constructor(app: PoserApp) {
    this.#app = app;
    this.#context = app.context;
  }

  get debugHandle(): { app: PoserApp; context: Context } {
    console.warn(`exposing controller innards`);
    return { app: this.#app, context: this.#context };
  }

  get renderer(): WebGLRenderer {
    return this.#app.context.renderer;
  }

  get clock(): PoserClock {
    return this.#app.context.clock;
  }

  get positions(): number[] {
    const positions: number[] = [];
    for (const actor of this.#app.context.actors.values()) {
      positions.push(actor.id);
    }
    return positions;
  }

  get #cameraController() {
    return this.#context.cameraController;
  }

  get cameraConfigs() {
    return CAMERA_CONFIGS;
  }

  get isPlaying(): boolean {
    return this.#context.state.playing === true;
  }

  get playCurrentTime(): number {
    return this.#context.clock.real - this.playStartTime;
  }

  set playCurrentTime(offset: number) {
    this.#app.seekTo(
      Math.min(
        this.playEndTime,
        Math.max(this.playStartTime, this.playStartTime + offset)
      )
    );
  }

  get playStartTime(): number {
    return this.#context.clock.playLimits
      ? this.#context.clock.playLimits[0]
      : 0;
  }

  get playEndTime(): number {
    return this.#context.clock.playLimits
      ? this.#context.clock.playLimits[1]
      : 0;
  }

  get playTotalTime(): number {
    return this.#context.clock.playPeriod ? this.#context.clock.playPeriod : 0;
  }

  play(): void {
    this.#context.state.playing = true;
  }

  pause(): void {
    this.#context.state.playing = false;
  }

  toggleLoop(): void {
    this.#context.clock.loop = !this.#context.clock.loop;
  }

  seekToTimestamp(realSeconds: number): void {
    this.#app.seekTo(realSeconds);
  }

  setCameraMode(mode: PublicCameraOption): void {
    switch (mode) {
      case "action":
        this.#cameraController?.setMode(CameraMode.ACTION);
        break;
      case "batter":
        this.#cameraController?.setMode(CameraMode.PERSONAL, false, {
          focusedID: 10,
        });
        break;
      case "pitcher":
        this.#cameraController?.setMode(CameraMode.PERSONAL, false, {
          focusedID: 1,
        });
        break;
      case "free":
        this.#cameraController?.setMode(CameraMode.MAP);
        this.#cameraController?.setLocation(CameraLandmark.HH);
        break;
    }
    this.#context.callbacks.onCameraChange({ mode, fromInteraction: false });
  }

  setBearerToken(bearerToken: string) {
    this.#context.workerChannel.setBearerToken(bearerToken);
  }

  addSubscriberToLoadingStatus(name: string, cb: (pctLoaded: number) => void) {
    this.#app.addPctLoadedSubscribers(name, cb);
  }

  addSubscriberToContextChange(name: string, cb: (context: Context) => void) {
    this.#app.addSubscribers(name, cb);
  }

  cleanUp() {
    this.#app.teardown();
  }
}
